export const BASE_URL = 'https://sohmcollectables.com';
export const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1643324895873-a186cb9acab2?w=1200&q=85';

export const PUBLIC_ROUTE_SEO = {
  '/': {
    title: 'SŌHM | Gold & Silver Collectibles with Transparent Pricing',
    description: 'Shop physical gold and silver collectibles with BIS purity details, live metal-linked pricing and digital authenticity certificates from SŌHM.',
    image: DEFAULT_IMAGE,
    schemas: [
      { '@context': 'https://schema.org', '@type': 'Organization', name: 'SŌHM', url: BASE_URL },
      { '@context': 'https://schema.org', '@type': 'WebSite', name: 'SŌHM', url: BASE_URL, inLanguage: 'en-IN' },
    ],
  },
  '/silver': {
    title: '925 Silver Jewellery Online India | SŌHM',
    description: 'Browse 925 silver jewellery online in India with transparent pricing across SŌHM silver chains, silver coins and silver bars, subject to current availability.',
    image: 'https://images.unsplash.com/photo-1681554913232-a501060263b0?w=1200&q=85',
    breadcrumbLabel: 'Silver',
  },
  '/gold': {
    title: 'Gold Collectibles & BIS Hallmarked Pieces | SŌHM',
    description: 'Explore BIS-hallmarked gold coins, bars and wearable collectibles with clear purity, weight and live metal-linked pricing in India.',
    image: DEFAULT_IMAGE,
    breadcrumbLabel: 'Gold',
  },
  '/custom': {
    title: 'Custom Gold & Silver Jewellery Requests | SŌHM',
    description: 'Request a custom gold or silver piece from SŌHM artisans using your reference image, preferred metal, weight and budget.',
    image: 'https://images.unsplash.com/photo-1581090465980-58ea88b43443?w=1200&q=85',
    breadcrumbLabel: 'Custom Jewellery',
  },
  '/rates': {
    title: 'Live Gold & Silver Rate | SŌHM',
    description: 'View the latest published gold and silver rate, historical price charts and transparent precious-metal pricing.',
    breadcrumbLabel: 'Live Rate',
  },
  '/about': {
    title: 'About SŌHM | Transparent Precious Metal Collecting',
    description: 'Learn how SŌHM combines physical gold and silver, BIS purity standards, serialized collectibles and transparent pricing for modern collectors.',
    breadcrumbLabel: 'About',
  },
  '/purity-standards': {
    title: 'BIS Gold & Silver Purity Standards | SŌHM',
    description: 'Understand BIS gold and silver fineness standards, including 24K, 22K, 18K, 999 fine silver and 925 sterling silver.',
    breadcrumbLabel: 'Purity Standards',
  },
  '/learn': {
    title: 'Gold & Silver Journal, News and Guides | SŌHM',
    description: 'Read factual gold and silver guides, verified precious-metal news and market context for collectors in India.',
    breadcrumbLabel: 'Journal',
  },
  '/faq': {
    title: 'Gold & Silver Collectibles FAQ | SŌHM',
    description: 'Answers about SŌHM pricing, BIS hallmarking, gold and silver purity, authenticity, drops, purchasing and insured shipping.',
    breadcrumbLabel: 'FAQ',
  },
  '/contact': {
    title: 'Contact SŌHM | Collectible and Order Support',
    description: 'Contact SŌHM for help with gold and silver collectibles, custom requests, orders and product information.',
    breadcrumbLabel: 'Contact',
  },
  '/returns': {
    title: 'Returns & Cancellations | SŌHM',
    description: 'Read the SŌHM cancellation, return and exchange policy for gold, silver, made-to-order and custom jewellery.',
    breadcrumbLabel: 'Returns & Cancellations',
  },
  '/calendar': {
    title: 'Gold & Silver Drop Calendar | SŌHM',
    description: 'View scheduled and live SŌHM releases for limited gold and silver collectibles.',
    breadcrumbLabel: 'Drop Calendar',
  },
  '/verify': {
    title: 'Verify a SŌHM Authenticity Certificate',
    description: 'Verify the digital authenticity certificate and serialized details for a SŌHM gold or silver collectible.',
    breadcrumbLabel: 'Verify Certificate',
  },
};

export const NOINDEX_PREFIXES = [
  '/admin', '/login', '/signup', '/checkout', '/account', '/chest',
  '/portfolio', '/orders', '/order-success', '/wishlist', '/pothdar',
  '/certificate', '/search',
];

export const canonicalUrl = (path = '/') => `${BASE_URL}${path === '/' ? '' : path}`;

export const createBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: canonicalUrl(item.path),
  })),
});

export const getRouteSeo = (path) => {
  const config = PUBLIC_ROUTE_SEO[path];
  if (!config) return null;
  const schemas = [...(config.schemas || [])];
  if (config.breadcrumbLabel) {
    schemas.push(createBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: config.breadcrumbLabel, path },
    ]));
  }
  return { ...config, path, schemas };
};

export const createProductSeo = (collectible, displayedPrice) => {
  const path = `/collectible/${collectible.collectible_id}`;
  const metal = collectible.metal_type === 'gold' ? 'Gold' : 'Silver';
  const purity = collectible.purity || collectible.purity_karat || `${collectible.purity_fineness || ''}`;
  const images = (collectible.images?.length ? collectible.images : [collectible.image_url]).filter(Boolean);
  const sku = collectible.serial_number || collectible.collectible_id;
  const price = Number(displayedPrice);
  const hasValidatedPrice = Number.isFinite(price) && price > 0;
  const description = hasValidatedPrice
    ? `${collectible.name}: ${purity} ${metal.toLowerCase()}, ${collectible.weight}g, priced at ₹${Math.round(price).toLocaleString('en-IN')}. BIS purity details and transparent pricing from SŌHM.`
    : `${collectible.name}: ${purity} ${metal.toLowerCase()}, ${collectible.weight}g. BIS purity details and transparent pricing from SŌHM.`;
  return {
    path,
    title: `${collectible.name} | SŌHM`,
    description,
    image: images[0] || DEFAULT_IMAGE,
    type: 'product',
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: collectible.name,
        description: collectible.description || description,
        image: images,
        sku,
        brand: { '@type': 'Brand', name: 'SŌHM' },
        material: `${purity} ${metal}`,
        weight: { '@type': 'QuantitativeValue', value: collectible.weight, unitCode: 'GRM' },
        ...(hasValidatedPrice ? { offers: {
          '@type': 'Offer',
          url: canonicalUrl(path),
          priceCurrency: 'INR',
          price: price.toFixed(2),
          availability: collectible.quantity_remaining > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          itemCondition: 'https://schema.org/NewCondition',
        } } : {}),
      },
      createBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: metal, path: `/${metal.toLowerCase()}` },
        { name: collectible.name, path },
      ]),
    ],
  };
};

export const createFaqSeo = (faqs) => {
  const base = getRouteSeo('/faq');
  return {
    ...base,
    schemas: [
      ...base.schemas,
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };
};